<template>
  <div id="staff">
    <Breadcrumbs
      :crumbs="[
        { name: getGlobals.info.school_name, link: `${getPathPrefix}/` },
        { name: getSectionName, link: `${getPathPrefix}/staff` },
      ]"
    />
    <div class="page-container">
      <div class="filters">
        <div class="search">
          <label
            for="search-input"
            class="sr-only"
            >{{ $t('pages.shared.search') }}</label
          >
          <text-input
            id="search-input"
            type="search"
            name="search"
            :model-value="search"
            :placeholder="$t('pages.shared.searchPlaceholder')"
            :disabled="!!memberName"
            @update:model-value="searchEvent"
          />
          <img
            src="../../assets/search.svg"
            class="search-icon"
            alt=""
          />
          <div
            v-if="memberName"
            class="member-tag"
            @click="removeMemberIdSearch"
          >
            <p>{{ memberName }}</p>
            <img
              src="../../assets/cms-close-icon.svg"
              class="close-icon"
              :alt="$t('pages.shared.close')"
            />
          </div>
        </div>
        <div
          class="sr-only"
          aria-live="polite"
        >
          {{ searchResultsMsg }}
        </div>
        <Filters
          v-if="!!filters.length"
          :filters="filters"
          :placeholder="$t('pages.staff.departmentsPlaceholder')"
          name="departments"
          @on-filter="onFilterChange"
        />
      </div>
      <LoadingIndicator v-if="loading" />
      <div
        v-for="(column, idx) in rows"
        v-else
        :key="idx"
        class="page-container-item"
      >
        <StaffCard
          v-for="member in column"
          :key="member.id"
          :member="member"
          :email="staffEmails"
          @open-contact-modal="handleOpenContactModal"
        />
      </div>

      <pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
      />
    </div>
    <StaffContactModal
      v-if="memberToContact"
      :member="memberToContact"
      @close-modal="handleOpenContactModal(null)"
    />
  </div>
</template>

<script>
import { mapGetters, useStore } from 'vuex';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import Filters from '../../components/global/FiltersDropdown.vue';
import Pagination from '../../components/global/Pagination.vue';
import StaffCard from '../../components/pages/staff/StaffCard';
import TextInput from '../../components/global/Forms/TextInput.vue';
import StaffContactModal from '../../components/pages/staff/StaffContactModal/StaffContactModal';
import { setupAndFetchStaff } from '../../helpers/section/sections.helper';

export default defineNuxtComponent({
  name: 'Staff',
  components: {
    StaffContactModal,
    Breadcrumbs,
    LoadingIndicator,
    Pagination,
    Filters,
    StaffCard,
    TextInput,
  },
  beforeRouteUpdate(to, from, next) {
    if (
      import.meta.client &&
      (to.query.page_no !== from.query.page_no ||
        !isEqual(to.query.filter_ids, from.query.filter_ids) ||
        to.query.search !== from.query.search ||
        to.query.id !== from.query.id)
    ) {
      this.fetchStaff(to.query);
      next();
    } else {
      next();
    }
  },
  async setup() {
    const store = useStore();
    const { data } = await useAsyncData('staff', () =>
      setupAndFetchStaff(store, useRoute().query, useNuxtApp().$axios),
    );
    useHead({
      title: store.getters.getSectionName,
    });
    return data.value;
  },
  data() {
    return {
      loading: false,
      staff: [],
      filters: [],
      currentPage: 0,
      totalPages: 0,
      memberName: null,
      memberToContact: null,
      search: '',
      searchDebounce: debounce(() => {
        this.searchCall();
      }, 500),
    };
  },
  computed: {
    ...mapGetters([
      'getStaff',
      'getLinks',
      'getGlobals',
      'getPathPrefix',
      'getCustomSections',
      'getSectionName',
      'getSettings',
    ]),
    ...mapGetters('translation', ['getLanguage']),
    rows() {
      return chunk(this.staff, 2);
    },
    staffEmails() {
      return this.getSettings.staffEmails || 'display_emails';
    },
    searchResultsMsg() {
      if (this.memberName) {
        return `${this.$t('pages.staff.memberFound', { amount: 1 })} ${this.$t('pages.staff.removeTag')}`;
      }

      if (this.search) {
        return this.$t('pages.staff.memberFound', { amount: this.total_entries });
      }

      return this.$t('pages.staff.filterByName');
    },
  },
  watch: {
    '$route.query.page_no': {
      handler(oldValue, newValue) {
        if (import.meta.client && oldValue !== newValue) {
          window.scrollTo(0, 0);
        }
      },
    },
  },
  methods: {
    async fetchStaff(query) {
      this.loading = true;
      this.totalPages = 0;
      const { staff, filters, totalPages, currentPage } = await setupAndFetchStaff(this.$store, query, this.$axios);
      this.staff = staff;
      this.filters = filters;
      this.totalPages = totalPages;
      this.currentPage = currentPage;
      this.loading = false;
    },
    onFilterChange(filterArr) {
      let filter_ids;
      if (filterArr.length > 0) {
        filter_ids = filterArr.join(',');
      }

      this.$router.replace({
        query: {
          ...this.$route.query,
          page_no: undefined,
          filter_ids,
        },
      });
    },
    handleOpenContactModal(member) {
      this.memberToContact = member;
    },
    searchEvent(text) {
      this.search = text;
      this.searchDebounce();
    },
    searchCall() {
      return navigateTo({
        query: {
          ...this.$route.query,
          page_no: undefined,
          search: this.search === '' ? undefined : this.search,
        },
      });
    },
    removeMemberIdSearch() {
      this.memberName = null;

      return navigateTo({
        query: {
          id: undefined,
        },
      });
    },
  },
});
</script>

<style scoped lang="scss">
#staff {
  .page-container-item {
    max-width: none;
    margin: 0 70px;
    padding: 0;
    @media screen and (max-width: 1000px) {
      border-bottom: none;
      margin: 0;
    }
  }
  .filters {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
    .search {
      position: relative;
      margin-top: 10px;
      margin-left: 5px;
      .search-icon {
        position: absolute;
        top: 14px;
        right: 10px;
      }
      .member-tag {
        background-color: #f3f3f3;
        border-radius: 16px;
        position: absolute;
        padding: 2px 7px;
        font-size: 12px;
        top: 10px;
        left: 8px;
        display: flex;
        cursor: pointer;
        .close-icon {
          margin-left: 10px;
          max-height: 8px;
          max-width: 8px;
          margin-top: 4px;
        }
      }
    }
  }
}
</style>
